import * as React from "react";

import {
  type BrushMode,
  brushWindow,
  clampTime,
  timeAtFraction,
} from "./brush";
import { DRAG_THRESHOLD_PX } from "./useTimelineGestures";
import {
  centerWindow,
  clampWindow,
  sameWindow,
  type TimeWindow,
  type WindowUpdate,
} from "./window";

interface Drag {
  mode: BrushMode;
  pointerId: number;
  /** Pointer x at the press, measured against `DRAG_THRESHOLD_PX`. */
  pressX: number;
  /** The strip's domain at the press; the whole drag is measured against it, even if the history grows meanwhile. */
  domain: TimeWindow;
  /** The instant under the press. */
  anchorMs: number;
  /** The window when the press began; every move is measured from it, and a cancelled drag goes back to it. */
  window: TimeWindow;
  /** Set once the pointer passes the threshold; the press is then a drag for the rest of the gesture. */
  moved: boolean;
  /** Where a move or resize last put the mark, clamped as `setTimeWindow` clamps it. */
  last: TimeWindow | null;
}

export interface BrushOptions {
  /** The strip's domain: the whole history, at least `MIN_SPAN` long. */
  domain: TimeWindow;
  timeWindow: TimeWindow;
  setTimeWindow: (update: WindowUpdate) => void;
}

export interface BrushGestures {
  /** The mode of the drag in progress, for the cursor; `null` when idle. */
  dragging: BrushMode | null;
  /**
   * Where the mark is while it is moved or resized, and after the release
   * until the window arrives; `null` otherwise. The window changes in a
   * transition that waits while the tracks load a new chunk, so the mark is
   * drawn from here to keep up with the pointer and not snap back on release.
   */
  draggedWindow: TimeWindow | null;
  /** A span being drawn on empty strip, not yet applied; `null` otherwise. */
  selection: TimeWindow | null;
  handlers: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  };
}

/**
 * Pointer gestures on the overview strip:
 * - a drag on the mark moves the window, keeping its span;
 * - a drag on one of its handles (`data-brush="start"` / `"end"`) moves that edge;
 * - a drag across empty strip draws a new span, applied on release;
 * - a click on empty strip centres the window there, keeping its span.
 *
 * A click on the mark or a handle does nothing. Moving and resizing change
 * the window as the pointer moves; a drawn span waits for the release, since
 * the view jumping to its first few pixels would be noise. A cancelled drag
 * (the browser took the pointer) puts the window back where it was.
 * `setTimeWindow` clamps every window to the extent, so the mark never leaves
 * the strip.
 *
 * One pointer at a time: a second finger's press is ignored until the first
 * lifts. The handlers belong on the strip's plot; pixels are read from it at
 * event time. The drag is gesture bookkeeping no render reads, so it lives
 * in a ref.
 */
export function useBrushGestures({
  domain,
  timeWindow,
  setTimeWindow,
}: BrushOptions): BrushGestures {
  const drag = React.useRef<Drag | null>(null);
  const [dragging, setDragging] = React.useState<BrushMode | null>(null);
  const [draggedWindow, setDraggedWindow] = React.useState<TimeWindow | null>(
    null,
  );
  const [selection, setSelection] = React.useState<TimeWindow | null>(null);

  // Once the window a drag dropped has arrived, the mark follows the window again.
  React.useEffect(() => {
    if (drag.current === null) {
      setDraggedWindow(null);
    }
  }, [timeWindow]);

  const timeAt = (
    event: React.PointerEvent<HTMLElement>,
    within: TimeWindow,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return timeAtFraction(
      within,
      rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0,
    );
  };

  /** The drag this event belongs to; `null` for another pointer or when idle. */
  const dragOf = (event: React.PointerEvent<HTMLElement>) =>
    drag.current?.pointerId === event.pointerId ? drag.current : null;

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || drag.current !== null) {
      return;
    }
    const handle = (event.target as Element).closest<HTMLElement>(
      "[data-brush]",
    );
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      mode: (handle?.dataset.brush as BrushMode | undefined) ?? "select",
      pointerId: event.pointerId,
      pressX: event.clientX,
      domain,
      anchorMs: timeAt(event, domain),
      window: timeWindow,
      moved: false,
      last: null,
    };
  };

  /** The window the drag asks for with the pointer at `event`. A drawn span stops at the strip's ends. */
  const requested = (current: Drag, event: React.PointerEvent<HTMLElement>) => {
    const pointerMs = timeAt(event, current.domain);
    return current.mode === "select"
      ? brushWindow(
          "select",
          current.window,
          clampTime(current.domain, current.anchorMs),
          clampTime(current.domain, pointerMs),
        )
      : brushWindow(current.mode, current.window, current.anchorMs, pointerMs);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const current = dragOf(event);
    if (!current) {
      return;
    }
    if (!current.moved) {
      if (Math.abs(event.clientX - current.pressX) < DRAG_THRESHOLD_PX) {
        return;
      }
      current.moved = true;
      setDragging(current.mode);
    }
    const next = requested(current, event);
    if (current.mode === "select") {
      setSelection(next);
    } else {
      current.last = clampWindow(next, current.domain);
      setDraggedWindow(current.last);
      setTimeWindow(next);
    }
  };

  const endDrag = (event: React.PointerEvent<HTMLElement>) => {
    const current = dragOf(event);
    if (!current) {
      return;
    }
    if (event.currentTarget.hasPointerCapture(current.pointerId)) {
      event.currentTarget.releasePointerCapture(current.pointerId);
    }
    drag.current = null;
    setDragging(null);
    setSelection(null);
    if (event.type !== "pointerup") {
      setDraggedWindow(null);
      if (current.moved && current.mode !== "select") {
        setTimeWindow(current.window);
      }
      return;
    }
    if (current.mode !== "select") {
      // The dropped window may still be on its way; the effect above lets go
      // of the mark when it lands. If it already has, let go now.
      if (current.last === null || sameWindow(current.last, timeWindow)) {
        setDraggedWindow(null);
      }
      return;
    }
    if (current.moved) {
      setTimeWindow(requested(current, event));
    } else {
      const at = clampTime(current.domain, current.anchorMs);
      setTimeWindow((window) => centerWindow(window, at));
    }
  };

  return {
    dragging,
    draggedWindow,
    selection,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
